import {
  AddIcon,
  DeleteIcon,
  DragHandleIcon,
  EditIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";

import linelist from "../../../media/directoryReport.png";
import file from "../../../media/fileTypeImg.png";
import pbi from "../../../media/powerBIReport.png";
import staticImg from "../../../media/staticReport.png";
import tableau from "../../../media/Tableau.png";

// ─── Sortable Card ────────────────────────────────────────────────────────────
const SortableCard = ({
  item,
  isActive,
  onItemClick,
  onEditItem,
  onDeleteItem,
  getImageSource,
  typeColors,
}) => {
  const {
    attributes,
    listeners, // 👈 attach ONLY to the drag handle
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 999 : "auto",
  };

  const badgeColor = typeColors[item.type] || "gray";
  const imageSrc = getImageSource(item.cardImg);

  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <Card
        cursor="pointer"
        onClick={() => onItemClick(item)}
        transition="all 0.2s"
        border="2px solid"
        borderColor={isActive ? "blue.500" : "gray.200"}
        bg={isActive ? "blue.50" : "white"}
        _hover={{
          transform: isDragging ? "none" : "translateY(-4px)",
          shadow: "lg",
          borderColor: "blue.400",
        }}
        position="relative"
        overflow="hidden"
        height="100%"
      >
        {/* Card Image */}
        <Box bg="bgColor" p={2}>
          <Image
            src={imageSrc}
            alt={item.title}
            objectFit="contain"
            height="140px"
            width="100%"
            borderRadius="xl"
          />
        </Box>

        <CardBody>
          <VStack align="start" spacing={2}>
            <HStack align="center" justify="space-between" spacing={2} w="100%">
              <Badge colorScheme={badgeColor} fontSize="xs">
                {item.type === "directory" ? "Folder" : item.type}
              </Badge>

              <HStack spacing={0.3}>
                {/* ✅ Drag handle — listeners attached here only */}
                <Tooltip label="Drag to reorder" placement="top">
                  <IconButton
                    icon={<DragHandleIcon />}
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                    aria-label="Drag to reorder"
                    border="none"
                    cursor="grab"
                    _active={{ cursor: "grabbing" }}
                    onClick={(e) => e.stopPropagation()} // prevent card click
                    {...listeners} // 👈 only the handle triggers drag
                  />
                </Tooltip>

                <Tooltip label="Edit Item" placement="top">
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditItem(item);
                    }}
                    aria-label="Edit item"
                    border="none"
                  />
                </Tooltip>

                <Tooltip label="Delete Item" placement="top">
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteItem(item);
                    }}
                    aria-label="Delete item"
                    border="none"
                  />
                </Tooltip>
              </HStack>
            </HStack>

            <Heading size="sm" noOfLines={2}>
              {item.title}
            </Heading>
            {item.subTitle && (
              <Text fontSize="sm" color="gray.600" noOfLines={1}>
                {item.subTitle}
              </Text>
            )}
            {item.desc && (
              <Text fontSize="sm" color="gray.500" noOfLines={2}>
                {item.desc}
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const AdminSectionCardsPanel = ({
  section,
  onItemClick,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onReorder, // 👈 new prop — call this to persist new order
  activeItemId,
}) => {
  const [activeId, setActiveId] = useState(null);

  const typeColors = {
    powerbi: "purple",
    tableau: "orange",
    directory: "blue",
    file: "gray",
    static: "teal",
  };

  const IMAGE_ASSETS = { pbi, linelist, staticImg, tableau, file };

  const getImageSource = (cardImg) => {
    if (!cardImg) return staticImg;
    if (cardImg.includes("://")) return cardImg;
    return IMAGE_ASSETS[cardImg] || staticImg;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // prevent accidental drags
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = section.items.findIndex((i) => i.id === active.id);
    const newIndex = section.items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(section.items, oldIndex, newIndex);

    onReorder?.(reordered); // 👈 lift new order up to parent
  };

  if (!section) {
    return (
      <Box flex={1} p={8} bg="white" minH="calc(100vh - 60px)">
        <VStack spacing={4} align="center" justify="center" h="100%">
          <Text fontSize="lg" color="gray.500">
            Select a section to view items
          </Text>
        </VStack>
      </Box>
    );
  }

  const activeItem = section.items?.find((i) => i.id === activeId);

  return (
    <Box flex={1} p={8} bg="white" minH="calc(100vh - 60px)" overflowY="auto">
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="lg" mb={2}>
            {section.title}
          </Heading>
          <Text color="gray.600">
            Manage items in this section ({section.items?.length || 0} items)
          </Text>
        </Box>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="teal"
          onClick={onAddItem}
          size="md"
        >
          Add Item
        </Button>
      </HStack>

      {section.items?.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={section.items.map((i) => i.id)}
            strategy={rectSortingStrategy}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              {section.items.map((item) => (
                <SortableCard
                  key={item.id}
                  item={item}
                  isActive={activeItemId === item.id}
                  onItemClick={onItemClick}
                  onEditItem={onEditItem}
                  onDeleteItem={onDeleteItem}
                  getImageSource={getImageSource}
                  typeColors={typeColors}
                />
              ))}
            </SimpleGrid>
          </SortableContext>

          {/* Ghost card shown while dragging */}
          <DragOverlay>
            {activeItem ? (
              <Box opacity={0.9} transform="scale(1.03)" shadow="2xl">
                <SortableCard
                  item={activeItem}
                  isActive={false}
                  onItemClick={() => {}}
                  onEditItem={() => {}}
                  onDeleteItem={() => {}}
                  getImageSource={getImageSource}
                  typeColors={typeColors}
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <VStack spacing={4} py={12} align="center">
          <Text fontSize="lg" color="gray.500">
            No items in this section yet
          </Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="teal"
            onClick={onAddItem}
            size="md"
          >
            Add First Item
          </Button>
        </VStack>
      )}

      {section?.footerImg && (
        <Box py={4}>
          <Image src={section?.footerImg} alt={section.title} w="100%" />
        </Box>
      )}
    </Box>
  );
};

export default AdminSectionCardsPanel;
